import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "../components/SEO";
import { FaCheckCircle, FaCog, FaTachometerAlt, FaTools, FaWrench } from "react-icons/fa";
import "./CarService.css";
import heroImage from "../assets/ds.webp";
import Footer from "../components/Footer";

const CarService = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedAdvancedServices, setSelectedAdvancedServices] = useState([]);
  const navigate = useNavigate();

  const servicePackages = {
    basic: {
      id: "basic",
      name: "Basic / Minor Service",
      services: [
        "Engine oil change",
        "Oil filter replacement",
        "Air filter cleaning (or replacement if needed)",
        "Fluids top-up (coolant, brake fluid, windscreen washer)",
        "Inspection of lights & indicators",
        "Battery health check",
        "Tyre pressure check"
      ]
    },
    intermediate: {
      id: "intermediate",
      name: "Intermediate Service",
      note: "Everything in basic, plus:",
      services: [
        "Air filter replacement",
        "Brake inspection (pads, rotors)",
        "Throttle body cleaning",
        "Computer diagnostics scan (OBD scan)"
      ]
    },
    full: {
      id: "full",
      name: "Full / Major Service",
      note: "Everything in intermediate, plus:",
      services: [
        "Spark plugs replacement",
        "Fuel filter replacement",
        "Cabin / AC filter replacement",
        "AC service (gas, compressor check, leakage test)",
        "Complete wiring inspection",
        "Throttle body & injectors cleaning",
        "Coolant flush",
        "Brake fluid flush",
        "Transmission oil change (if needed)",
        "Differential oil change (for RWD/4x4)",
        "Full OBD-II diagnostics"
      ]
    }
  };

  const advancedServices = [
    { id: 1, name: "Wiring Inspection", description: "Complete electrical wiring system inspection", category: "Electrical" },
    { id: 2, name: "ECU Tuning / Remapping", description: "Engine Control Unit optimization and performance tuning", category: "Performance" },
    { id: 3, name: "Engine Compression Test", description: "Cylinder compression testing for engine health assessment", category: "Engine" },
    { id: 4, name: "Smoke Test for Vacuum Leaks", description: "Professional smoke testing to detect vacuum leaks", category: "Diagnostics" },
    { id: 5, name: "Hybrid Battery Health Test", description: "Comprehensive hybrid battery system health check", category: "Hybrid" }
  ];

  const handleCategorySelect = (categoryId) => {
    if (selectedCategory === categoryId) setSelectedCategory(null);
    else setSelectedCategory(categoryId);
  };

  const handleAdvancedToggle = (service) => {
    const isSelected = selectedAdvancedServices.find((s) => s.id === service.id);
    if (isSelected) {
      setSelectedAdvancedServices(selectedAdvancedServices.filter((s) => s.id !== service.id));
    } else {
      setSelectedAdvancedServices([...selectedAdvancedServices, service]);
    }
  };

  const handleRequestService = () => {
    if (!selectedCategory && selectedAdvancedServices.length === 0) return;

    let subject = "Car Service Request";
    let message = "Hello,\n\nI am interested in the following car service:\n\n";

    if (selectedCategory) {
      const packageData = servicePackages[selectedCategory];
      subject = `Car Service Request - ${packageData.name}`;
      message += `📋 Selected Service Package:\n${packageData.name}\n\n`;

      if (packageData.note) message += `${packageData.note}\n\n`;

      message += `Services Included:\n${packageData.services.map(service => `• ${service}`).join('\n')}\n\n`;
    }

    if (selectedAdvancedServices.length > 0) {
      message += `🔧 Additional Advanced Services:\n\n${selectedAdvancedServices
        .map(service => `• ${service.name} (${service.category})\n  ${service.description}`)
        .join('\n\n')}\n\n`;
    }

    message += "Please provide me with detailed information about pricing, availability, and estimated service time.\n\nThank you!";

    navigate("/contact", {
      state: { subject, message }
    });
  };

  const canonical = "https://carforensic.uk/car-service";

  const carServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Car Servicing and Maintenance",
    "url": canonical,
    "areaServed": ["Kent", "Maidstone", "Gillingham", "Sevenoaks", "Sittingbourne"],
    "provider": {
      "@type": "AutoRepair",
      "name": "Car Forensics LTD",
      "url": "https://carforensic.uk/",
      "telephone": "+447501911970",
      "email": "carforensicsltd@gmail.com"
    },
    "description":
      "Mobile car servicing and maintenance packages across Kent including Maidstone, Gillingham, Sevenoaks and Sittingbourne."
  };

  return (
    <>
      <SEO
        title="Car Forensics LTD | Car Service"
        description="Professional car servicing and maintenance packages across Kent — including Maidstone, Gillingham, Sevenoaks and Sittingbourne. Choose a service package and request a quote from Car Forensics LTD."
        canonical={canonical}
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(carServiceSchema)}
        </script>
      </Helmet>

      <div className="car-service-page">
        {/* Hero Section */}
        <section
          className="car-service-hero"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="heros-overlay"></div>
          <div className="car-service-hero-content">
            <div className="hero-icon-wrapper">
              <FaWrench className="hero-icon rotating" />
            </div>
            <h1>Professional Car Service</h1>
            <p>Complete vehicle servicing and maintenance packages</p>
            <div className="hero-stats">
              <div className="stat-item">
                <FaTachometerAlt />
                <span>Expert Technicians</span>
              </div>
              <div className="stat-item">
                <FaTools />
                <span>Quality Parts</span>
              </div>
              <div className="stat-item">
                <FaCheckCircle />
                <span>Reliable Service</span>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="car-service-section">
          <div className="car-service-container">
            <div className="section-intro">
              <h2>Choose Your Service Package</h2>
              <p>Select a service package that suits your vehicle's needs, and add optional advanced checks.</p>
            </div>

            <div className="car-service-content-wrapper">
              <div className="services-selection-area">
                <div className="packages-section">
                  <h3 className="section-heading">Service Packages</h3>
                  <div className="packages-grid">

                    <div
                      className={`package-card ${selectedCategory === "basic" ? "selected" : ""}`}
                      onClick={() => handleCategorySelect("basic")}
                    >
                      <div className="package-header">
                        <div className="package-icon basic">
                          <FaWrench />
                        </div>
                        <h4>{servicePackages.basic.name}</h4>
                      </div>
                      <div className="package-services">
                        <ul>
                          {servicePackages.basic.services.map((service, index) => (
                            <li key={index}>
                              <FaCheckCircle className="check-icon" />
                              <span>{service}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="package-select-indicator">
                        {selectedCategory === "basic" ? "✓ Selected" : "Click to Select"}
                      </div>
                    </div>

                    <div
                      className={`package-card ${selectedCategory === "intermediate" ? "selected" : ""}`}
                      onClick={() => handleCategorySelect("intermediate")}
                    >
                      <div className="package-header">
                        <div className="package-icon intermediate">
                          <FaCog />
                        </div>
                        <h4>{servicePackages.intermediate.name}</h4>
                      </div>
                      <div className="package-services">
                        <ul>
                          {servicePackages.intermediate.services.map((service, index) => (
                            <li key={index}>
                              <FaCheckCircle className="check-icon" />
                              <span>{service}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="package-select-indicator">
                        {selectedCategory === "intermediate" ? "✓ Selected" : "Click to Select"}
                      </div>
                    </div>

                    <div
                      className={`package-card ${selectedCategory === "full" ? "selected" : ""}`}
                      onClick={() => handleCategorySelect("full")}
                    >
                      <div className="package-header">
                        <div className="package-icon full">
                          <FaTools />
                        </div>
                        <h4>{servicePackages.full.name}</h4>
                      </div>
                      <div className="package-services">
                        <ul>
                          {servicePackages.full.services.map((service, index) => (
                            <li key={index}>
                              <FaCheckCircle className="check-icon" />
                              <span>{service}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="package-select-indicator">
                        {selectedCategory === "full" ? "✓ Selected" : "Click to Select"}
                      </div>
                    </div>

                  </div>
                </div>

                <div className="advanced-section">
                  <h3 className="section-heading">Advanced Checks (Optional)</h3>
                  <div className="advanced-services-grid">
                    {advancedServices.map((service) => {
                      const isSelected = selectedAdvancedServices.find((s) => s.id === service.id);
                      return (
                        <label
                          key={service.id}
                          className={`service-checkbox-card ${isSelected ? "selected" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => handleAdvancedToggle(service)}
                          />
                          <div className="checkbox-custom">
                            <FaCheckCircle className="check-icon" />
                          </div>
                          <div className="serv-info">
                            <div className="serv-details-wrapper">
                              <span className="serv-name">{service.name}</span>
                              <span className="serv-category-tag">{service.category}</span>
                            </div>
                            <p className="serv-description">{service.description}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="summary-sidebar">
                <div className="summary-card">
                  <h3 className="summary-title">
                    <FaCheckCircle className="summary-icon" />
                    Your Selection
                  </h3>

                  {!selectedCategory && selectedAdvancedServices.length === 0 ? (
                    <div className="empty-state">
                      <p>No service selected yet</p>
                      <span>Select a service package or advanced checks</span>
                    </div>
                  ) : (
                    <>
                      {selectedCategory && (
                        <>
                          <div className="selected-package">
                            <h4>Service Package:</h4>
                            <div className="package-name">{servicePackages[selectedCategory].name}</div>
                          </div>
                          <div className="summary-divider"></div>
                        </>
                      )}

                      {selectedAdvancedServices.length > 0 && (
                        <>
                          <div className="selected-advanced">
                            <h4>Advanced Services:</h4>
                            <div className="selected-services-list">
                              {selectedAdvancedServices.map((service) => (
                                <div key={service.id} className="selected-service-item">
                                  <div className="service-detail">
                                    <span className="item-name">{service.name}</span>
                                    <span className="item-category">{service.category}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="summary-divider"></div>
                        </>
                      )}

                      <div className="selection-summary">
                        {selectedCategory && (
                          <div className="summary-row">
                            <span>Service Package</span>
                            <span className="summary-value">1</span>
                          </div>
                        )}
                        {selectedAdvancedServices.length > 0 && (
                          <div className="summary-row">
                            <span>Advanced Services</span>
                            <span className="summary-value">{selectedAdvancedServices.length}</span>
                          </div>
                        )}
                      </div>

                      <button className="cta-button-service" onClick={handleRequestService}>
                        <span>Request Service</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>

                      <div className="info-note">
                        <p>Request a detailed quote for your selected services with pricing and availability.</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="contact-quick-card">
                  <h4>Need Assistance?</h4>
                  <p>Our experts are here to help</p>
                  <a href="tel:+447501911970" className="phone-link-service">
                    📞 +447501911970
                  </a>

                  {/* internal link fixed */}
                  <Link to="/contact" className="contact-link-service">
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="car-service-info-section">
          <div className="car-service-container">
            <div className="infoss-grid">
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaCheckCircle />
                </div>
                <h3>Quality Service</h3>
                <p>Professional car servicing with genuine parts and experienced technicians.</p>
              </div>
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaTachometerAlt />
                </div>
                <h3>Mobile Service</h3>
                <p>We come to your location for convenient and hassle-free car servicing.</p>
              </div>
              <div className="infoss-card">
                <div className="infoss-icon">
                  <FaTools />
                </div>
                <h3>Comprehensive Checks</h3>
                <p>Thorough inspection and maintenance to keep your vehicle running smoothly.</p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default CarService;
